// AI Provider Panel Component
import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { AIResponse } from '../../types';
import { formatTime } from '../../utils/helpers';

interface AIProviderPanelProps {
  responses: AIResponse[];
  provider: 'openai' | 'gemini';
  isSessionActive: boolean;
  isDarkMode?: boolean;
}

export const AIProviderPanel: React.FC<AIProviderPanelProps> = ({
  responses,
  provider,
  isSessionActive,
  isDarkMode = false
}) => {
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopyResponse = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(id);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className={`h-[600px] flex flex-col rounded-xl border p-6 transition-colors ${
      isDarkMode
        ? 'bg-gray-900 border-gray-700 text-gray-100'
        : 'bg-white border-gray-200 text-gray-900 shadow-lg'
    }`}>
      {/* Header with Logo and Title */}
      <div className={`mb-4 pb-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center space-x-3">
          {/* Provider Logo */}
          <div className="w-8 h-8 flex items-center justify-center">
            {provider === 'openai' ? (
              <img src="/openai.svg" alt="OpenAI" className="w-8 h-8" />
            ) : (
              <img src="/gemini.svg" alt="Gemini" className="w-8 h-8" />
            )}
          </div>
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {provider === 'openai' ? 'Powered by OpenAI' : 'Powered by Gemini'}
          </h2>
        </div>
      </div>

      {/* Response List */}
      <div className="flex-1 overflow-hidden">
        {responses.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <p className="text-center">
              {isSessionActive 
                ? 'AI responses will appear here when interview questions are detected' 
                : 'Start your interview to get AI-powered response suggestions'
              }
            </p>
          </div>
        ) : (
          <div className="h-full overflow-y-auto space-y-6 pr-2">
            {responses.filter(r => r && r.id && r.question && r.responses[provider]).slice().reverse().map((response) => {
              const aiResponse = response.responses[provider];
              if (!aiResponse) return null;

              return (
                <div
                  key={response.id}
                  className="space-y-3"
                >
                  {/* Question */}
                  <div className={`p-3 rounded-lg border ${
                    isDarkMode
                      ? 'border-gray-700 bg-gray-800/70'
                      : 'border-white/30 bg-white/10 backdrop-blur-md'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Question</span>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatTime(response.timestamp)}</span>
                        <button
                          onClick={() => handleCopyResponse(response.question || '', `${response.id}-question`)}
                          className={`p-1 rounded transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                          title="Copy question"
                        >
                          {copiedText === `${response.id}-question` ? (
                            <Check className="w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </div>
                    <p className={`text-sm leading-relaxed font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      {response.question || 'No question text available'}
                    </p>
                  </div>

                  {/* AI Response */}
                  <div className={`p-3 rounded-lg border ${
                    isDarkMode
                      ? 'border-gray-700 bg-gray-800/70'
                      : 'border-white/30 bg-white/10 backdrop-blur-md'
                  }`}>
                    {/* Response Header with Logo */}
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        {provider === 'openai' ? (
                          <img src="/openai.svg" alt="OpenAI" className="w-5 h-5" />
                        ) : (
                          <img src="/gemini.svg" alt="Gemini" className="w-5 h-5" />
                        )}
                      </div>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {provider === 'openai' ? 'OpenAI Response' : 'Gemini Response'}
                      </span>
                    </div>
                    
                    {/* Response Text with Copy Button */}
                    <div className="relative">
                      <div className="absolute top-0 right-0">
                        <button
                          onClick={() => handleCopyResponse(aiResponse.text || '', `${response.id}-${provider}`)}
                          className={`p-2 rounded transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                          title="Copy response"
                        >
                          {copiedText === `${response.id}-${provider}` ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div className={`text-sm leading-relaxed pr-8 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        <div className={`prose prose-sm max-w-none prose-headings:my-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-pre:my-2 prose-code:before:content-[''] prose-code:after:content-[''] ${
                          isDarkMode ? 'prose-invert' : ''
                        }`}>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                          >
                            {aiResponse.text || 'No response text available'}
                          </ReactMarkdown>
                        </div>
                        {response.isStreaming && (
                          <span className="inline-block ml-1">
                            <span className="animate-pulse">|</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};
